import { useMutation } from '@apollo/client/react'
import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'

import type { TakeawayFeedback } from '../graphqlEnums'

const SetTakeawayFeedbackDocument = graphql(/* GraphQL */ `
  mutation SetTakeawayFeedback($input: SetTakeawayFeedbackInput!) {
    setTakeawayFeedback(input: $input) {
      ... on SetTakeawayFeedbackSuccess {
        takeaway {
          __typename
          id
          feedback
        }
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

interface SetFeedbackOptions {
  takeawayId: string
  channelId: string
  organizationId: string
  feedback: TakeawayFeedback | null
}

interface UseSetTakeawayFeedbackResult {
  setFeedback: (options: SetFeedbackOptions) => Promise<void>
  loading: boolean
}

export function useSetTakeawayFeedback(): UseSetTakeawayFeedbackResult {
  const [mutate, { loading }] = useMutation(SetTakeawayFeedbackDocument)

  const setFeedback = async ({
    takeawayId,
    channelId,
    organizationId,
    feedback,
  }: SetFeedbackOptions): Promise<void> => {
    try {
      await mutate({
        variables: {
          input: { takeawayId, channelId, organizationId, feedback },
        },
        optimisticResponse: {
          setTakeawayFeedback: {
            __typename: 'SetTakeawayFeedbackSuccess',
            takeaway: {
              __typename: 'Takeaway',
              id: takeawayId,
              feedback,
            },
          },
        },
        update: (_cache, { data }) => {
          const result = data?.setTakeawayFeedback
          if (result?.__typename === 'VoidMutationError') {
            // Throw so Apollo rolls back the optimistic feedback value;
            // a resolved error-typed union member would otherwise leave the
            // optimistic update committed in the cache.
            throw new Error(result.message)
          }
        },
      })
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : 'Failed to save feedback',
      )
    }
  }

  return { setFeedback, loading }
}
